import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../integrations/supabase/client';

const { FiSun, FiCoffee, FiMoon, FiPackage, FiAlertCircle } = FiIcons;

const Products = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('status', 'Published')
        .order('created_at', { ascending: true });

      if (error) throw error;
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to map roast level to UI styles
  const getProductStyles = (roastLevel) => {
    const level = roastLevel?.toLowerCase() || '';
    if (level.includes('dark')) {
      return {
        icon: FiMoon,
        color: "from-gray-700 to-amber-800",
        bgColor: "bg-gray-50",
        flavorProfile: "Low acidity, chocolate notes, smoky finish",
        arabicFlavorProfile: "حمضية منخفضة، نكهات الشوكولاتة، طعم مدخن"
      };
    } else if (level.includes('light')) {
      return {
        icon: FiSun,
        color: "from-yellow-400 to-orange-500",
        bgColor: "bg-yellow-50",
        flavorProfile: "Bright acidity, floral aroma, citrus undertones",
        arabicFlavorProfile: "حمضية مشرقة، عطر زهري، نكهات حمضيات"
      };
    } else {
      // Default to Medium
      return {
        icon: FiCoffee,
        color: "from-amber-500 to-orange-600",
        bgColor: "bg-amber-50",
        flavorProfile: "Balanced acidity, caramel sweetness",
        arabicFlavorProfile: "حمضية متوازنة، حلاوة الكراميل"
      };
    }
  };

  return (
    <section id="products" className="py-20 bg-gradient-to-b from-green-50 to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6">
            {t('products.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('products.subtitle')}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-800"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white/50 rounded-2xl">
            <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No products available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {products.map((product, index) => {
              const styles = getProductStyles(product.roast_level);
              
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -15, transition: { duration: 0.3 } }}
                  className={`${styles.bgColor} rounded-2xl shadow-xl overflow-hidden flex flex-col group`}
                >
                  <div className="relative">
                    <motion.img 
                      src={product.image_url || "https://placehold.co/400x300?text=Bondeega"}
                      alt={product.name}
                      className="w-full h-72 object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    />
                    <div className={`absolute top-0 left-0 w-full h-full bg-gradient-to-t ${styles.color} opacity-20 group-hover:opacity-0 transition-opacity duration-300`}></div>
                    
                    {/* Coffee steam effect on hover */}
                    <motion.div 
                      className="absolute top-4 right-4"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                    >
                      <div className="relative coffee-steam">
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-2 h-2 bg-white rounded-full"
                            animate={{
                              y: [-10, -30],
                              opacity: [0, 1, 0],
                              x: [0, Math.sin(i) * 5]
                            }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              delay: i * 0.2,
                              ease: "easeOut"
                            }}
                          />
                        ))}
                      </div>
                    </motion.div>
                  </div>
                  
                  <div className={`p-6 text-white bg-gradient-to-r ${styles.color}`}>
                    <div className="flex items-center justify-between mb-2">
                      <motion.div
                        whileHover={{ scale: 1.2, rotate: 10 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <SafeIcon icon={styles.icon} className="w-8 h-8" />
                      </motion.div>
                       <span className="flex items-center text-sm bg-white/20 px-3 py-1 rounded-full">
                          <SafeIcon icon={FiPackage} className="w-4 h-4 mr-2" />
                          {product.size}
                        </span>
                    </div>
                    <h3 className="text-2xl font-bold">{product.name}</h3>
                    {/* Fallback to English name if arabic_description isn't formatted as name yet, or clean up db structure later */}
                    {/* For now we will display description logic below */}
                  </div>

                  <div className="p-6 space-y-4 flex-grow flex flex-col">
                    <p className="text-gray-700 leading-relaxed flex-grow">
                      {isRTL ? (product.arabic_description || product.description) : product.description}
                    </p>
                    
                    <div>
                      <h4 className="font-semibold text-green-800 mb-1">
                        {isRTL ? 'النكهة:' : `${t('products.flavorProfile')}:`}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {isRTL ? styles.arabicFlavorProfile : styles.flavorProfile}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <span className="text-sm font-medium text-green-700">
                        {product.roast_level} {t('products.roast')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default Products;